import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, UserCircle, FileUser, CalendarClock, CalendarOff, Briefcase, BellRing } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Avatar,
} from '@vnetwork/ui/primitives';
import { useAuth } from '@/auth/AuthProvider';
import { useMe } from '@/api/me';
import styles from './account-menu.module.css';

/**
 * Account entry points in the menu (VCards / My Incomes / Regulations are
 * reached from the Account landing nav cards, not here).
 */
const ACCOUNT_ITEMS: { label: string; path: string; Icon: typeof UserCircle }[] = [
  { label: 'My Account', path: '/account', Icon: UserCircle },
  { label: 'Detailed Info', path: '/account/info', Icon: FileUser },
  { label: 'Workday Accounts', path: '/account/workdays', Icon: CalendarClock },
  { label: 'Day-off Accounts', path: '/account/dayoffs', Icon: CalendarOff },
  { label: 'Business Accounts', path: '/account/businesses', Icon: Briefcase },
  { label: 'On-call', path: '/account/oncall', Icon: BellRing },
];

/**
 * Topbar account menu — identity (name + position • department) with the account
 * sub-pages and sign out. These account functions used to live in the sidebar;
 * they're consolidated here to keep the sidebar to actual product modules.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: me } = useMe();

  const emp = me?.employee;
  const name = emp?.employee_name || user?.name || user?.email || '—';
  const email = emp?.employee_email_company || emp?.employee_email || user?.email;
  const subtitle = [emp?.position_name, emp?.department_name].filter(Boolean).join(' • ');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={styles.trigger} aria-label="Account menu">
          <Avatar name={name} size={28} appearance="solid" tone="accent" />
          <span className={styles.identity}>
            <span className={styles.name}>{name}</span>
            {subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}
          </span>
          <ChevronDown size={14} color="var(--vn-fg-muted)" style={{ flexShrink: 0 }} />
        </button>
      </DropdownMenuTrigger>
      {/* minWidth: 224 is structural sizing — no token equivalent */}
      <DropdownMenuContent align="end" style={{ minWidth: 224 }}>
        {/* gap: 2px is sub-token precision; --vn-space-0=0, --vn-space-1=4px — no closer token */}
        <div style={{ padding: 'var(--vn-space-2) var(--vn-space-3)', display: 'grid', gap: '2px' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>{name}</span>
          {subtitle ? (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{subtitle}</span>
          ) : null}
          {email ? (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {email}
            </span>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        {ACCOUNT_ITEMS.map(({ label, path, Icon }) => (
          <DropdownMenuItem key={path} onSelect={() => navigate(path)}>
            <Icon size={16} /> {label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={logout}>
          <LogOut size={16} /> {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
